import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Header from '../../components/navigation/Header';

const ConfirmEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('pending'); // pending | verifying | success | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Check if this is a callback from the email verification link
    // Supabase appends token_hash and type to the URL
    const tokenHash = searchParams?.get('token_hash');
    const type = searchParams?.get('type');

    if (tokenHash && type === 'email') {
      setStatus('verifying');
      supabase?.auth?.verifyOtp({ token_hash: tokenHash, type: 'email' })?.then(({ error }) => {
          if (error) {
            setStatus('error');
            setErrorMessage(error?.message || 'Verification failed. The link may have expired.');
          } else {
            setStatus('success');
            setTimeout(() => navigate('/auth', { replace: true }), 2000);
          }
        });
    }
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Header user={null} onNavigate={() => {}} onPurchaseCredits={() => {}} onLogout={() => {}} />
      <div className="flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-500 shadow-lg mb-4">
            <Icon name="Camera" size={28} color="white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Angle</h1>
          <p className="text-gray-500 text-sm mt-1">Find your best angle with AI</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
          {status === 'pending' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-5">
                <Icon name="Mail" size={32} color="#7c3aed" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Check your email</h2>
              <p className="text-gray-500 text-sm mb-6">
                We've sent a confirmation link to your email address. Click the link to verify your account and get started.
              </p>
              <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={16} color="#7c3aed" className="mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-purple-700">
                    Didn't receive the email? Check your spam folder. The link expires in 24 hours.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/auth', { replace: true })}
                className="w-full py-3 px-4 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm"
              >
                Back to Sign In
              </button>
            </motion.div>
          )}

          {status === 'verifying' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-5">
                <Icon name="Loader2" size={32} color="#3b82f6" className="animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying your email…</h2>
              <p className="text-gray-500 text-sm">Please wait while we confirm your account.</p>
            </motion.div>
          )}

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-5">
                <Icon name="CheckCircle" size={32} color="#16a34a" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Email confirmed!</h2>
              <p className="text-gray-500 text-sm">Your account is verified. Redirecting you to sign in…</p>
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-5">
                <Icon name="XCircle" size={32} color="#dc2626" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verification failed</h2>
              <p className="text-gray-500 text-sm mb-6">{errorMessage}</p>
              <button
                onClick={() => navigate('/auth', { replace: true })}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all shadow-md text-sm"
              >
                Back to Sign In
              </button>
            </motion.div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ConfirmEmailPage;
